import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import clsx from 'clsx'
import React, { useId, useMemo, useRef, useEffect, useState } from 'react'
import './ApplyV2SelectSingle.scss'

interface Option {
    value: string | number
    label?: string | null
}

interface Props {
    value?: string | number | boolean | null | undefined
    label?: string
    onChange?: (value: string | number) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    onInput?: (value: string) => void
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    errorMessage?: string | null | boolean | React.ReactNode
    children?: React.ReactNode
    isRequired?: boolean
    placeholder?: string
    autoFocus?: boolean
    isShowErrorMessage?: boolean
    description?: React.ReactNode
    options: Option[]
    onInputChange?: (value: string) => string
}

const baseClass = 'ApplyV2SelectSingle'

function ApplyV2SelectSingle({
    value,
    label,
    onChange,
    onFocus,
    onBlur,
    onInput,
    isDisabled = false,
    className = '',
    id,
    name,
    isLoading = false,
    errorMessage,
    children,
    isRequired = false,
    placeholder = '',
    autoFocus,
    isShowErrorMessage = true,
    description,
    options,
    onInputChange,
}: Props) {
    const defaultId = useId()
    const { getTitle } = useApplyV2NavTranslations()
    const [isOpen, setIsOpen] = React.useState(false)
    const [searchValue, setSearchValue] = React.useState('')
    const [isInputFocused, setIsInputFocused] = React.useState(false)
    const [focusedOptionIndex, setFocusedOptionIndex] = React.useState(-1)
    const [isKeyboardNavigation, setIsKeyboardNavigation] = React.useState(false)
    const [menuPosition, setMenuPosition] = React.useState<'bottom' | 'top'>('bottom')
    const selectRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const optionRefs = useRef<(HTMLDivElement | null)[]>([])

    const optionsWithLabels = useMemo(
        () =>
            (options ?? []).map((o) => ({
                value: o.value,
                label: o.label ?? o.value.toString(),
            })),
        [options]
    )

    const selectedOption = optionsWithLabels.find((o) => o.value === value)
    const selectedValueLabel = selectedOption?.label || ''

    const filteredOptions = useMemo(() => {
        if (!searchValue) return optionsWithLabels

        return optionsWithLabels.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
    }, [optionsWithLabels, searchValue])

    const selectedOptionIndex = useMemo(() => {
        return filteredOptions.findIndex((option) => option.value === value)
    }, [filteredOptions, value])

    const setCursorToEnd = () => {
        if (inputRef.current) {
            const length = inputRef.current.value.length
            inputRef.current.setSelectionRange(length, length)
        }
    }

    const calculateMenuPosition = () => {
        if (!selectRef.current) return 'bottom'

        const selectRect = selectRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - selectRect.bottom
        const spaceAbove = selectRect.top

        const estimatedMenuHeight = Math.min(filteredOptions.length * 41 + 16, 300)

        if (spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
            return 'top'
        }

        return 'bottom'
    }

    const updateMenuPosition = () => {
        if (isOpen) {
            setMenuPosition(calculateMenuPosition())
        }
    }

    useEffect(() => {
        if (isOpen) {
            updateMenuPosition()
        }
    }, [isOpen, filteredOptions.length])

    useEffect(() => {
        if (isOpen && menuRef.current && selectedOptionIndex >= 0) {
            const selectedOptionElement = optionRefs.current[selectedOptionIndex]
            if (selectedOptionElement) {
                selectedOptionElement.scrollIntoView({
                    block: 'center',
                })
            }
            setFocusedOptionIndex(selectedOptionIndex)
        }
    }, [isOpen, selectedOptionIndex])

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('scroll', updateMenuPosition, true)
            window.addEventListener('resize', updateMenuPosition)

            return () => {
                window.removeEventListener('scroll', updateMenuPosition, true)
                window.removeEventListener('resize', updateMenuPosition)
            }
        }
    }, [isOpen])

    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
        e.stopPropagation()
        if (!isDisabled && !isLoading) {
            if (!isOpen) {
                setIsOpen(true)
            }
            setTimeout(() => {
                inputRef.current?.focus()
                setCursorToEnd()
            }, 0)
        }
    }

    const handleSelectClick = () => {
        if (!isDisabled && !isLoading && !isInputFocused) {
            setIsOpen(true)
            setTimeout(() => {
                inputRef.current?.focus()
                setCursorToEnd()
            }, 0)
        }
    }

    const handleOptionClick = (optionValue: string | number) => {
        onChange?.(optionValue)
        setIsOpen(false)
        setSearchValue('')
        setIsInputFocused(false)
        setFocusedOptionIndex(-1)
        setIsKeyboardNavigation(false)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value
        if (onInputChange) {
            value = onInputChange(value)
        }
        setSearchValue(value)
        onInput?.(value)

        setFocusedOptionIndex(-1)
        setIsKeyboardNavigation(false)

        if (!isOpen) {
            setIsOpen(true)
        }
    }

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsInputFocused(true)
        if (!isDisabled) {
            setIsOpen(true)
        }
        onFocus?.(e)

        setTimeout(() => {
            setCursorToEnd()
        }, 0)
    }

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsInputFocused(false)
        setTimeout(() => {
            if (!selectRef.current?.contains(document.activeElement)) {
                setIsOpen(false)
                setSearchValue('')
                setFocusedOptionIndex(-1)
                setIsKeyboardNavigation(false)
                onBlur?.(e)
            }
        }, 150)
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setIsKeyboardNavigation(true)
                if (!isOpen) {
                    setIsOpen(true)
                    setFocusedOptionIndex(0)
                } else {
                    const nextIndex = focusedOptionIndex >= filteredOptions.length - 1 ? 0 : focusedOptionIndex + 1
                    setFocusedOptionIndex(nextIndex)
                }
                break

            case 'ArrowUp':
                e.preventDefault()
                setIsKeyboardNavigation(true)
                if (!isOpen) {
                    setIsOpen(true)
                    setFocusedOptionIndex(filteredOptions.length - 1)
                } else {
                    const prevIndex = focusedOptionIndex <= 0 ? filteredOptions.length - 1 : focusedOptionIndex - 1
                    setFocusedOptionIndex(prevIndex)
                }
                break

            case 'Enter':
                e.preventDefault()
                setIsKeyboardNavigation(true)
                if (focusedOptionIndex >= 0 && focusedOptionIndex < filteredOptions.length) {
                    handleOptionClick(filteredOptions[focusedOptionIndex].value)
                } else if (filteredOptions.length > 0) {
                    handleOptionClick(filteredOptions[0].value)
                }
                break

            case 'Escape':
                setIsOpen(false)
                setSearchValue('')
                setFocusedOptionIndex(-1)
                setIsKeyboardNavigation(false)
                inputRef.current?.blur()
                break

            case 'Tab':
                setIsOpen(false)
                setSearchValue('')
                setFocusedOptionIndex(-1)
                setIsKeyboardNavigation(false)
                break

            default:
                setIsKeyboardNavigation(false)
                break
        }
    }

    const handleOptionMouseEnter = (index: number) => {
        if (!isKeyboardNavigation) {
            setFocusedOptionIndex(index)
        }
    }

    const handleMenuMouseMove = () => {
        setIsKeyboardNavigation(false)
    }

    useEffect(() => {
        if (focusedOptionIndex >= 0 && menuRef.current && optionRefs.current[focusedOptionIndex]) {
            optionRefs.current[focusedOptionIndex]?.scrollIntoView({
                block: 'nearest',
            })
        }
    }, [focusedOptionIndex])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setSearchValue('')
                setFocusedOptionIndex(-1)
                setIsKeyboardNavigation(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        if (!isOpen) {
            setSearchValue('')
            setFocusedOptionIndex(-1)
            setIsKeyboardNavigation(false)
        }
    }, [isOpen])

    useEffect(() => {
        optionRefs.current = optionRefs.current.slice(0, filteredOptions.length)
    }, [filteredOptions])

    const displayValue = isOpen ? searchValue : selectedValueLabel

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${baseClass}--open`]: isOpen,
                [`${baseClass}--focused`]: isInputFocused,
            })}
            ref={selectRef}
            data-error={!!errorMessage ? 'true' : 'false'}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__input-wrapper`, {
                    [`${baseClass}__input-wrapper--disabled`]: isDisabled,
                    [`${baseClass}__input-wrapper--loading`]: isLoading,
                    [`${baseClass}__input-wrapper--error`]: !!errorMessage,
                    [`${baseClass}__input-wrapper--open`]: isOpen,
                    [`${baseClass}__input-wrapper--focused`]: isInputFocused,
                })}
            >
                <div
                    className={clsx(`${baseClass}__select`, {
                        [`${baseClass}__select--error`]: !!errorMessage,
                        [`${baseClass}__select--loading`]: isLoading,
                        [`${baseClass}__select--open`]: isOpen,
                        [`${baseClass}__select--focused`]: isInputFocused,
                    })}
                    onClick={handleSelectClick}
                >
                    <input
                        ref={inputRef}
                        type="text"
                        id={id ? id.toString() : defaultId}
                        name={name}
                        value={displayValue}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        onClick={handleInputClick}
                        disabled={isDisabled}
                        placeholder={placeholder}
                        autoFocus={autoFocus}
                        autoComplete="off"
                        className={clsx(`${baseClass}__input`, {
                            [`${baseClass}__input--error`]: !!errorMessage,
                            [`${baseClass}__input--loading`]: isLoading,
                            [`${baseClass}__input--focused`]: isInputFocused,
                        })}
                    />
                    <div
                        className={clsx(`${baseClass}__arrow`, {
                            [`${baseClass}__arrow--open`]: isOpen,
                        })}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M11.7579 5.90901C11.5235 5.6747 11.1436 5.6747 10.9093 5.90901L8.00026 8.81808L5.09119 5.90901C4.85688 5.6747 4.47698 5.6747 4.24266 5.90901C4.00835 6.14333 4.00835 6.52323 4.24266 6.75754L7.576 10.0909C7.81031 10.3252 8.19021 10.3252 8.42452 10.0909L11.7579 6.75754C11.9922 6.52323 11.9922 6.14333 11.7579 5.90901Z"
                                fill="#19191A"
                            />
                        </svg>
                    </div>
                </div>

                {isOpen && (
                    <div
                        className={clsx(`${baseClass}__menu`, {
                            [`${baseClass}__menu--keyboard-navigation`]: isKeyboardNavigation,
                            [`${baseClass}__menu--top`]: menuPosition === 'top',
                            [`${baseClass}__menu--bottom`]: menuPosition === 'bottom',
                        })}
                        ref={menuRef}
                        onMouseMove={handleMenuMouseMove}
                    >
                        {filteredOptions.length === 0 ? (
                            <div className={`${baseClass}__no-options`}>{getTitle('5815', 'No results found')}</div>
                        ) : (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={option.value}
                                    ref={(el) => (optionRefs.current[index] = el)}
                                    className={clsx(`${baseClass}__option`, {
                                        [`${baseClass}__option--selected`]: option.value === value,
                                        [`${baseClass}__option--focused`]: index === focusedOptionIndex,
                                        [`${baseClass}__option--disabled`]: isDisabled,
                                    })}
                                    onClick={() => handleOptionClick(option.value)}
                                    onMouseEnter={() => handleOptionMouseEnter(index)}
                                >
                                    <div>{option.label}</div>
                                    {option.value === value && (
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M14.464 3.52165C14.7282 3.77809 14.7345 4.20015 14.4781 4.46436L6.71336 12.4644C6.58781 12.5937 6.41523 12.6667 6.23497 12.6667C6.05471 12.6667 5.88213 12.5937 5.75658 12.4644L1.52129 8.10072C1.26485 7.83652 1.27115 7.41445 1.53536 7.15802C1.79956 6.90158 2.22163 6.90788 2.47806 7.17209L6.23497 11.0428L13.5213 3.53572C13.7777 3.27152 14.1998 3.26522 14.464 3.52165Z"
                                                fill="#1D2E50"
                                            />
                                        </svg>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {description && <div className={`${baseClass}__description`}>{description}</div>}
            {errorMessage && isShowErrorMessage && (
                <div className={`${baseClass}__error`}>
                    <div className={`${baseClass}__error-icon`}>
                        <WarningErrorIcon />
                    </div>
                    <div className={`${baseClass}__error-text`}>{errorMessage}</div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2SelectSingle
